import { Ref } from 'vue';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { AxiosError, AxiosResponse } from 'axios';

import { api } from './_http';

// 한국주식 - 핫랭킹 카테고리 조회
export interface HotrankingCategoryResponse {
	hot_ranking: HotRankingType[];
}
export interface HotRankingType {
	hot_ranking_pk: string;
	hot_ranking_name: string;
	hot_ranking_enrollment_date: string;
}
export const useGetHotrankingCategoryApi_KR = (params: GetHotrankingCategoryParamsType) => {
	const url = '/v1/front/getHotrankingCategory';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingCategoryData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data.data,
	});
};

// 한국주식 - 핫랭킹 탭 조회
type HotrankingTabParams = Ref<{
	hot_ranking_pk: string;
}>;
type HotrankingTabResponse = any;
export const useGetHotrankingTabApi_KR = (params: HotrankingTabParams, enabled: Ref<boolean>) => {
	const url = '/v1/front/getHotrankingTab';
	return useQuery<AxiosResponse, AxiosError, HotrankingTabResponse>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data.data,
		enabled: enabled,
	});
};

// 한국주식 - 핫랭킹 데이터 조회
type GetHotrankingParamsType_KR = Ref<{
	hot_ranking_pk: string;
	division_top: string;

	page?: number;
}>;
type HotrankingMaster = {
	file_path: string;
	master_name: string;
	master_pk: string;
	opm: string;
	score: string;
};
export type HotRankingItem = {
	bookmark: string;
	event_name: string;
	event_pk: string;
	event_score: number;
	event_ticker: string;
	event_code?: string;
	file_path: string;
	master: HotrankingMaster[];
};
type HotrankingResponse = {
	hot_ranking: HotRankingItem[];
	hot_ranking_count: number;
};
export const useGetHotrankingApi_KR = (params: GetHotrankingParamsType_KR, enabled: Ref<boolean>) => {
	const url = '/v1/front/getHotranking';
	return useQuery<AxiosResponse, AxiosError, HotrankingResponse>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data.data,
		enabled: enabled,
		placeholderData: keepPreviousData,
	});
};

export const useInfiniteHotrankingApi_KR = (params: GetHotrankingParamsType_KR, enabled: Ref<boolean>) => {
	const url = '/v1/front/getHotranking';

	return useInfiniteQuery<{ data: HotrankingResponse }, AxiosError, HotrankingResponse>({
		queryKey: [url, params],
		queryFn: async ({ pageParam = 1 }) => {
			const res = await api.get<AxiosResponse<HotrankingResponse>>(url, {
				...params.value,
				page: pageParam,
			});
			return res.data;
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage: { data: HotrankingResponse }, allPages: { data: HotrankingResponse }[]) => {
			const totalItem = lastPage.data.hot_ranking_count;
			const currentItem = allPages.length * 10;
			const isNext = totalItem > currentItem ? true : null;

			return isNext && allPages.length + 1;
		},
		select: (res) => {
			const reulst = res.pages.reduce<HotrankingResponse>(
				(prev, cur) => {
					return {
						hot_ranking: [...prev.hot_ranking, ...cur.data.hot_ranking],
						hot_ranking_count: cur.data.hot_ranking_count,
					};
				},
				{ hot_ranking: [], hot_ranking_count: 0 }
			);

			return reulst;
		},
		enabled: enabled,
		gcTime: 1000 * 60 * 3,
		staleTime: 1000 * 60 * 3,
	});
};

// 한국주식 - 투자리포트 pk 불러오기
type GetInvestmentReportParamsType = Ref<{
	stock: string;
	master: string;
	score: string;
}>;
type GetInvestmentReportDataType = {
	master_investment_pk: number;
};
export const useGetInvestmentReportApi = (params: GetInvestmentReportParamsType) => {
	const url = '/v1/front/getInvestmentReport';
	return useQuery<AxiosResponse, AxiosError, GetInvestmentReportDataType[]>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => res.data.data,
		enabled: false,
	});
};

// 미국주식 - 핫랭킹 카테고리 조회
type GetHotrankingCategoryParamsType = Ref<{
	hot_ranking_category_pk?: number | string;
}>;
export type GetHotrankingCategoryData = {
	hotranking_category: hotrankingCategory;
};
type hotrankingCategory = {
	[key: string]: hotrankingCategorys[];
};
export type hotrankingCategorys = {
	hot_ranking_category_pk: number;
	hot_ranking_description: string;
	hot_ranking_image_path: string;
	hot_ranking_name: string;
	hot_ranking_pk: number;
};
export const useGetHotrankingCategoryApi = (params: GetHotrankingCategoryParamsType) => {
	const url = '/v1/front/usa/getHotrankingCategory';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingCategoryData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
	});
};

// 미국주식 - 핫랭킹 탭 조회
type GetHotrankingTabParamsType = Ref<{
	hot_ranking_pk: number | string;
}>;
export type hotRankingBanner = {
	banner_mo_url: string;
	hot_ranking_url: string;
	hot_ranking_banner_image_mo_path: string;
	hot_ranking_banner_image_pc_path: string;
};
export type hotRankingTab = {
	[key: string]: string[];
};
export type GetHotrankingTabData = {
	hot_ranking_banner: hotRankingBanner;
	hot_ranking_tab: hotRankingTab;
};
export const useGetHotrankingTabApi = (params: GetHotrankingTabParamsType) => {
	const url = '/v1/front/usa/getHotrankingTab';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingTabData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
	});
};

// 미국주식 - 핫랭킹 데이터 조회
type GetHotrankingParamsType = Ref<{
	hot_ranking_pk: number | string;
	division_top?: string;
	division_mid?: string;
}>;
export type GetHotrankingData = {
	hot_ranking: GetHotrankingKey;
};
type GetHotrankingKey = {
	[key: string]: GetHotrankingValue;
};
export type GetHotrankingValue = {
	bookmark: number;
	event_name: string;
	event_pk: number;
	event_ticker: string;
	event_score: number;
	file_path: string;
	master: Master[];
};
type Master = {
	file_path: string;
	master_name: string;
	master_pk: number;
};
export const useGetHotrankingApi = (params: GetHotrankingParamsType, enabled: Ref<boolean>) => {
	const url = '/v1/front/usa/getHotranking';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingValue[]>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return Object.values(res.data?.data.hot_ranking);
		},
		placeholderData: keepPreviousData,
		enabled: enabled,
	});
};

// 일본주식 - 핫랭킹 카테고리 조회
export const useGetHotrankingCategoryApi_JPN = (params: GetHotrankingCategoryParamsType) => {
	const url = '/v1/front/jpn/getHotrankingCategory';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingCategoryData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
	});
};

// 일본주식 - 핫랭킹 탭 조회
export const useGetHotrankingTabApi_JPN = (params: GetHotrankingTabParamsType) => {
	const url = '/v1/front/jpn/getHotrankingTab';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingTabData>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return res.data?.data;
		},
	});
};

// 일본주식 - 핫랭킹 데이터 조회
export const useGetHotrankingApi_JPN = (params: GetHotrankingParamsType, enabled: Ref<boolean>) => {
	const url = '/v1/front/jpn/getHotranking';
	return useQuery<AxiosResponse, AxiosError, GetHotrankingValue[]>({
		queryKey: [url, params],
		queryFn: () => api.get(url, params.value),
		select: (res) => {
			return Object.values(res.data?.data.hot_ranking);
		},
		placeholderData: keepPreviousData,
		enabled: enabled,
	});
};
